import { useCallback, useState } from 'react';
import { useLazyGetRiskInsightsSitesQuery } from 'src/app/portfolio/state/api/riskInsightsEngineSlice';
import { downloadTextAsFile } from 'src/shared/helpers/downloadAsFile';
import { buildSiteListFilename } from './buildSiteListFilename';
import { runSiteListDownload, type DownloadProgress } from './runSiteListDownload';

// Backend caps page_size at 100, so the full portfolio is fetched in batches.
const DOWNLOAD_PAGE_SIZE = 100;
// Cap concurrent requests so we don't melt the browser's HTTP connection
// pool or the backend on very large portfolios.
const DOWNLOAD_CONCURRENCY = 10;
// Above this site count, swap the button's spinner for a visible progress
// bar so the user knows the download is making progress.
export const PROGRESS_BAR_THRESHOLD = 1000;

export type UseSiteListDownload = {
    download: () => Promise<void>;
    isDownloading: boolean;
    downloadError: string | null;
    downloadProgress: DownloadProgress | null;
};

export const useSiteListDownload = (businessCentralId: string): UseSiteListDownload => {
    const [fetchSites] = useLazyGetRiskInsightsSitesQuery();
    const [isDownloading, setIsDownloading] = useState(false);
    const [downloadError, setDownloadError] = useState<string | null>(null);
    const [downloadProgress, setDownloadProgress] = useState<DownloadProgress | null>(null);

    const download = useCallback(async () => {
        if (!businessCentralId) return;
        setIsDownloading(true);
        setDownloadError(null);
        setDownloadProgress(null);
        try {
            await runSiteListDownload({
                fetchPage: async (page, pageSize) => {
                    const result = await fetchSites({ businessCentralId, page, pageSize }).unwrap();
                    return {
                        items: result.organisation?.sites?.items ?? [],
                        total: result.organisation?.sites?.pagination.totalItems ?? 0,
                    };
                },
                downloadFile: downloadTextAsFile,
                onProgress: setDownloadProgress,
                pageSize: DOWNLOAD_PAGE_SIZE,
                concurrency: DOWNLOAD_CONCURRENCY,
                progressThreshold: PROGRESS_BAR_THRESHOLD,
                filename: buildSiteListFilename(businessCentralId, new Date()),
            });
        } catch (error) {
            console.error('Failed to download site list:', error);
            setDownloadError('Could not download the site list. Please try again.');
        } finally {
            setIsDownloading(false);
            setDownloadProgress(null);
        }
    }, [businessCentralId, fetchSites]);

    return { download, isDownloading, downloadError, downloadProgress };
};
